/**
 * Dimmed full-viewport mask with a hole cut out around a target
 * element. Used by FirstRunTour to point at a nav item / banner / etc.
 *
 * Implemented with two boxes:
 *   1. A full-screen fixed div with rgba(0,0,0,0.55)
 *   2. Four sibling divs around the target rect (top / right / bottom
 *      / left) drawn as part of the same mask using CSS clip-path
 *
 * `getBoundingClientRect` measures the target on mount + on
 * ResizeObserver fire — keeps the hole locked even when the layout
 * reflows.
 */
import { useEffect, useState } from 'react';

interface TourSpotlightProps {
  /** CSS selector for the element to spotlight. Re-queried whenever
   *  the selector changes; nothing happens if the element isn't found
   *  yet (the tour will retry on the next animation frame). */
  selector: string;
  /** Padding around the target rect in pixels — gives the hole some
   *  breathing room so the spotlight ring doesn't kiss the element. */
  padding?: number;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function TourSpotlight({ selector, padding = 8 }: TourSpotlightProps): React.JSX.Element | null {
  const [rect, setRect] = useState<Rect | null>(null);

  // Find the target + measure. Retries on rAF until found (selector
  // might not exist yet if we just navigated to the target route).
  useEffect(() => {
    let cancelled = false;
    let rafId = 0;
    const findAndMeasure = () => {
      if (cancelled) return;
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) {
        rafId = requestAnimationFrame(findAndMeasure);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      // Watch for size/position changes (route transitions, scroll).
      const ro = new ResizeObserver(() => {
        const r2 = el.getBoundingClientRect();
        if (!cancelled) setRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
      });
      ro.observe(el);
      // Also watch for window resize.
      const onResize = () => {
        const r2 = el.getBoundingClientRect();
        if (!cancelled) setRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
      };
      window.addEventListener('resize', onResize);
      window.addEventListener('scroll', onResize, true);
      // Cleanup on selector change / unmount.
      return () => {
        ro.disconnect();
        window.removeEventListener('resize', onResize);
        window.removeEventListener('scroll', onResize, true);
      };
    };
    rafId = requestAnimationFrame(findAndMeasure);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [selector]);

  if (!rect) {
    // Element not found yet — render a uniform dim mask so the
    // coach's view is still focused on Brain's narration.
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'none',
          zIndex: 9000,
          transition: 'opacity 200ms ease',
        }}
        aria-hidden="true"
      />
    );
  }

  const hole = {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };

  // Four overlay boxes forming a frame around the hole.
  const dim = 'rgba(0, 0, 0, 0.55)';
  const overlay: React.CSSProperties = {
    position: 'fixed',
    background: dim,
    pointerEvents: 'none',
    transition: 'all 200ms ease',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, pointerEvents: 'none' }} aria-hidden="true">
      {/* Top */}
      <div style={{ ...overlay, top: 0, left: 0, right: 0, height: hole.top }} />
      {/* Bottom */}
      <div style={{ ...overlay, top: hole.top + hole.height, left: 0, right: 0, bottom: 0 }} />
      {/* Left */}
      <div style={{ ...overlay, top: hole.top, left: 0, width: hole.left, height: hole.height }} />
      {/* Right */}
      <div style={{ ...overlay, top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height }} />
      {/* Ring around the hole (subtle glow). */}
      <div
        style={{
          position: 'fixed',
          top: hole.top - 2,
          left: hole.left - 2,
          width: hole.width + 4,
          height: hole.height + 4,
          border: '2px solid rgba(39, 174, 96, 0.85)',
          borderRadius: 8,
          boxShadow: '0 0 24px 4px rgba(39, 174, 96, 0.35)',
          pointerEvents: 'none',
          transition: 'all 200ms ease',
        }}
      />
    </div>
  );
}
